// STUB data for the design/navigation pass. Static, in-memory — swapped for
// real Firestore reads when the features are built (post first-paying-club).
// Types here are lightweight UI shapes, NOT the canonical Zod models.
import type { PricingRuleInput } from '@upadelmatch/core'

/**
 * Fixture dates are RELATIVE TO TODAY, never hardcoded.
 *
 * They used to be literals: `dayLabel: 'Hoy', dateISO: '2026-07-04'`. On
 * 2026-08-07 `/partido/m1` read **"Hoy · sáb, 4 jul"** — the label and the date
 * contradicting each other by a month, and the "Hoy" filter still matching it.
 * Every design review, screenshot and persona walk since had been reading a
 * self-contradictory screen.
 *
 * `check:test-date-rot` could never have caught this: it scans TEST files for
 * `new Date('…')` literals, and these are `dateISO` strings in a source file.
 * Relative dates close the class instead of extending the checker — a fixture
 * computed from `today` cannot rot.
 */
function daysFromToday(offset: number): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + offset)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Two independent axes (see docs/data-model.md): `privacy` = who can join
// (visibility), `matchType` = play style (casual vs competitive/ranked-label).
export type MatchPrivacy = 'Abierto' | 'Privado'
export type MatchType = 'Casual' | 'Competitivo'

export interface MockMatch {
  id: string
  place: string
  city: string
  /** null = unknown (real matches until proximity ships) — cards omit the segment. */
  distanceKm: number | null
  dayLabel: string
  /** yyyy-mm-dd — powers date-input prefill in Editar partido (design stub). */
  dateISO: string
  timeLabel: string
  /** Level requirement as a nivel 0–7 range; both null = Indiferente (any). */
  levelMin: number | null
  levelMax: number | null
  position: 'Derecha' | 'Revés' | 'Indiferente'
  privacy: MatchPrivacy
  matchType: MatchType
  /**
   * Seats, in roster order. `identity` + `createdByName` carry the ladder
   * (spec/02 § Seats & identity) so the stub renders the same shapes the real
   * doc produces — a fixture that cannot occur in production is how two bugs
   * hid this week.
   */
  players: {
    name: string
    src?: string
    uid?: string
    identity?: 'placeholder' | 'guest' | 'account'
    createdByName?: string | null
  }[]
  /** Companions the organizer brings who aren't registered (mirrors the real
   *  model's availableSlots math: free = capacity - players - companions). */
  offAppCompanions?: number
  capacity: number
  organizer: string
  organizerMatches: number
  organizerNoShows: number
  courtReserved: boolean
  /** Who can join — absent/`'open'` = anyone (mock matches are all open). */
  genderCategory?: 'open' | 'men' | 'women' | 'mixed'
  /**
   * VIEW-MODEL status — deliberately NOT the persisted set, which is
   * `active | cancelled | completed | full` (see `core/models/match.ts`).
   *
   * `past` is derived by clock in `use-matches.ts`, and `completed` has no twin
   * here because a result can only be recorded after kickoff — so every
   * completed match is already `past`. "Played" vs "nobody recorded" is read off
   * `result`, not off this field. Do not align the two sets.
   */
  status: 'active' | 'full' | 'past' | 'cancelled'
  note?: string
  /** €/jugador — the WhatsApp 💰 slot; absent/null = not stated. */
  cost?: number | null
  /** Recorded post-match result (in-session store; real build persists it). */
  result?: { winners: string[]; pairing: number }
  /** Organizer's group invite (spec/02 #11). Added 2026-08-08 — the shape was
   *  missing from the stub, so Editar silently dropped edits to it and still
   *  reported "Cambios guardados". A stub that cannot hold a field the product
   *  has will always produce a lying screen somewhere. */
  whatsappGroupUrl?: string | null
}

export interface MockClub {
  id: string
  name: string
  city: string
  address: string
  courts: number
  surface: string
  indoor: boolean
  distanceKm: number
  /** Display + wa.me numbers — stage-1 booking happens by phone/WhatsApp. */
  phone: string
  whatsapp: string
  amenities: string[]
}

export interface MockContact {
  id: string
  name: string
  /** Nivel 0–7 (see shared/levels.ts). */
  level: number
  city: string
}

// Location the discovery feed is centred on. Design stub: the LocationBar lets
// the player switch it; real geolocation + geocoding lands with the feature.
export const MOCK_LOCATIONS = ['Granada', 'Armilla', 'Maracena', 'La Zubia', 'Madrid'] as const

// Distance from each pickable location to the mock data's Granada anchor (km).
// Makes the location axis REAL over mocks: switching location shifts every
// distance (and Madrid honestly empties the feed) instead of being decorative.
export const LOCATION_OFFSET_KM: Record<string, number> = {
  'Cerca de mí': 0,
  Granada: 0,
  Armilla: 2.4,
  Maracena: 3.1,
  'La Zubia': 4.6,
  Madrid: 418,
}

/** Distance of an item (anchored in Granada) as seen from `location`. */
export function distanceFrom(location: string, baseKm: number): number {
  return Math.round((baseKm + (LOCATION_OFFSET_KM[location] ?? 0)) * 10) / 10
}

export const mockProfile = {
  name: 'Marce Ruiz',
  city: 'Granada',
  phone: '+34 600 123 123',
  /** Nivel 0–7 (see shared/levels.ts). */
  level: 3.4,
  position: 'Revés',
  matches: 47,
  streak: 6,
  noShows: 0,
  /** Level confidence 0–100 (spec/05). The returning-player fiction is a
   *  calibrated account; a hydrated real identity overrides both fields. */
  levelReliability: 85 as number | null,
  levelCalibrated: true,
}

export const mockMatches: MockMatch[] = [
  {
    id: 'm1',
    place: 'Padel Sur',
    city: 'Granada',
    distanceKm: 1.2,
    dayLabel: dayLabelFrom(daysFromToday(0)),
    dateISO: daysFromToday(0),
    timeLabel: '19:00',
    levelMin: 3.0,
    levelMax: 4.0,
    position: 'Revés',
    privacy: 'Abierto',
    matchType: 'Casual',
    players: [{ name: 'Carlos Medina' }, { name: 'Ana Pérez' }, { name: 'Jorge López' }],
    capacity: 4,
    organizer: 'Carlos Medina',
    organizerMatches: 12,
    organizerNoShows: 0,
    courtReserved: true,
    status: 'active',
    note: 'Somos 3, buscamos 1 a revés. Tenemos pista.',
  },
  {
    id: 'm2',
    place: 'Maristas Padel',
    city: 'Granada',
    distanceKm: 2.8,
    dayLabel: dayLabelFrom(daysFromToday(1)),
    dateISO: daysFromToday(1),
    timeLabel: '20:30',
    levelMin: 4.0,
    levelMax: 5.0,
    position: 'Indiferente',
    privacy: 'Abierto',
    matchType: 'Competitivo',
    players: [
      { name: 'Lucía Ramos', uid: 'u-lucia', identity: 'account' },
      { name: 'Dani García', uid: 'u-dani', identity: 'account' },
      // Seat somebody typed a name into and nobody has claimed yet.
      { name: 'Javi Soler', uid: 'ph_demo1', identity: 'placeholder', createdByName: 'Lucía Ramos' },
    ],
    capacity: 4,
    organizer: 'Lucía Ramos',
    organizerMatches: 34,
    organizerNoShows: 1,
    courtReserved: false,
    // A real category on the FIXTURES, added 2026-08-12. Every mock match was
    // 'open', so the audience chip never rendered under the dev bypass — a
    // design walk on the stub would score the feature as if it did not exist.
    genderCategory: 'mixed',
    status: 'active',
    note: 'Partido igualado, nivel 4–5. Falta reservar pista.',
  },
  {
    id: 'm3',
    place: 'Padel IA',
    city: 'Granada',
    distanceKm: 4.1,
    dayLabel: dayLabelFrom(daysFromToday(7)),
    dateISO: daysFromToday(7),
    timeLabel: '11:00',
    levelMin: 0.0,
    levelMax: 2.0,
    position: 'Derecha',
    privacy: 'Abierto',
    matchType: 'Casual',
    players: [{ name: 'Marta Soler' }, { name: 'Pablo Vega' }, { name: 'Inés Torres' }],
    capacity: 4,
    organizer: 'Marta Soler',
    organizerMatches: 8,
    organizerNoShows: 0,
    courtReserved: true,
    // A real category on the FIXTURES, added 2026-08-12. Every mock match was
    // 'open', so the audience chip never rendered under the dev bypass — a
    // design walk on the stub would score the feature as if it did not exist.
    genderCategory: 'women',
    status: 'active',
  },
]

export const mockPastMatches: MockMatch[] = [
  {
    // Organizer-OWNED past match — powers the "Repetir partido" walk.
    id: 'p0',
    place: 'Maristas Padel',
    city: 'Granada',
    distanceKm: 2.8,
    dayLabel: dayLabelFrom(daysFromToday(-9)),
    dateISO: daysFromToday(-9),
    timeLabel: '21:00',
    levelMin: 3.0,
    levelMax: 4.0,
    position: 'Derecha',
    privacy: 'Abierto',
    matchType: 'Casual',
    players: [{ name: 'Marce Ruiz' }, { name: 'Carlos Medina' }, { name: 'Ana Pérez' }, { name: 'Jorge López' }],
    capacity: 4,
    organizer: 'Marce Ruiz',
    organizerMatches: 47,
    organizerNoShows: 0,
    courtReserved: true,
    cost: 2.5,
    status: 'past',
    note: 'Nuestro jueves de siempre.',
  },
  {
    id: 'p1',
    place: 'Padel Sur',
    city: 'Granada',
    distanceKm: 1.2,
    dayLabel: dayLabelFrom(daysFromToday(-5)),
    dateISO: daysFromToday(-5),
    timeLabel: '19:00',
    levelMin: 3.0,
    levelMax: 4.0,
    position: 'Indiferente',
    privacy: 'Abierto',
    matchType: 'Casual',
    players: [{ name: 'Carlos Medina' }, { name: 'Ana Pérez' }, { name: 'Jorge López' }, { name: 'Marce Ruiz' }],
    capacity: 4,
    organizer: 'Carlos Medina',
    organizerMatches: 12,
    organizerNoShows: 0,
    courtReserved: true,
    status: 'past',
  },
]

export const mockClubs: MockClub[] = [
// CONTACT IS DELIBERATELY UNEVEN ACROSS THESE FOUR, for two reasons.
//
// 1. A LANDLINE CANNOT RECEIVE WHATSAPP. Until 2026-08-10 every mock club had
//    `whatsapp` set to its own 958… landline — the exact mistake the real
//    product splits `phone` and `whatsapp` to prevent. A fixture that models
//    something impossible teaches the wrong thing to whoever reads it next.
//
// 2. THE STUB HAS TO BE ABLE TO SHOW EVERY STATE. Club detail says a different
//    thing per channel, and with all four clubs identical only ONE branch was
//    reachable in the dev bypass — so two of the three could be checked by
//    unit test but never seen. Now:
//      c1, c2 → phone + a real mobile WhatsApp   ("te confirma por WhatsApp")
//      c3     → phone only                        ("Llama al club para reservar")
//      c4     → neither                           ("no ha publicado un teléfono")
//    c4 is the state a club can now CHOOSE by switching both toggles off, so
//    it is the one most worth being able to look at.
  { id: 'c1', name: 'Padel Sur', city: 'Granada', address: 'C. Palencia 12', courts: 4, surface: 'Césped artificial', indoor: false, distanceKm: 1.2, phone: '+34 958 111 222', whatsapp: '34600111222', amenities: ['Parking', 'Vestuarios', 'Alquiler de palas'] },
  { id: 'c2', name: 'Maristas Padel', city: 'Granada', address: 'Av. de Cádiz 30', courts: 6, surface: 'Cristal panorámica', indoor: true, distanceKm: 2.8, phone: '+34 958 333 444', whatsapp: '34600333444', amenities: ['Parking', 'Vestuarios', 'Cafetería', 'Clases'] },
  { id: 'c3', name: 'Padel IA', city: 'Granada', address: 'Pol. Juncaril', courts: 3, surface: 'Césped artificial', indoor: true, distanceKm: 4.1, phone: '+34 958 555 666', whatsapp: '', amenities: ['Vestuarios', 'Cafetería', 'Torneos'] },
  { id: 'c4', name: 'Open Padel Genil', city: 'Granada', address: 'Camino de Ronda 145', courts: 5, surface: 'Hormigón', indoor: false, distanceKm: 3.3, phone: '', whatsapp: '', amenities: ['Parking', 'Clases', 'Alquiler de palas'] },
]

export const mockContacts: MockContact[] = [
  { id: 'k1', name: 'Carlos Medina', level: 3.2, city: 'Granada' },
  { id: 'k2', name: 'Ana Pérez', level: 3.6, city: 'Granada' },
  { id: 'k3', name: 'Jorge López', level: 3.8, city: 'Granada' },
  { id: 'k4', name: 'Lucía Ramos', level: 4.1, city: 'Granada' },
]

// The player's own organized match (privado — kept OUT of the open discovery
// feed) — powers the organizer-mode design (editar/cancelar/abrir al público).
export const mockMyMatch: MockMatch = {
  id: 'm4',
  place: 'Maristas Padel',
  city: 'Granada',
  distanceKm: 2.8,
  dayLabel: dayLabelFrom(daysFromToday(5)),
  dateISO: daysFromToday(5),
  timeLabel: '21:00',
  levelMin: 3.0,
  levelMax: 4.0,
  position: 'Derecha',
  privacy: 'Privado',
  matchType: 'Casual',
  players: [{ name: 'Marce Ruiz' }, { name: 'Carlos Medina' }, { name: 'Ana Pérez' }],
  capacity: 4,
  organizer: 'Marce Ruiz',
  organizerMatches: 47,
  organizerNoShows: 0,
  courtReserved: true,
  status: 'active',
  note: 'Nuestro jueves de siempre. Falta 1 a derecha.',
}

// A match the player JOINED but does not organize (kept out of the discovery
// feed mocks so feed demos stay stable) — powers the "Apuntado" card state.
export const mockJoinedMatch: MockMatch = {
  id: 'm5',
  place: 'Open Padel Genil',
  city: 'Granada',
  distanceKm: 3.3,
  dayLabel: dayLabelFrom(daysFromToday(3)),
  dateISO: daysFromToday(3),
  timeLabel: '10:00',
  levelMin: null,
  levelMax: null,
  position: 'Indiferente',
  privacy: 'Abierto',
  matchType: 'Casual',
  players: [{ name: 'Lucía Ramos' }, { name: 'Dani García' }, { name: 'Marce Ruiz' }],
  capacity: 4,
  organizer: 'Lucía Ramos',
  organizerMatches: 34,
  organizerNoShows: 1,
  courtReserved: true,
  status: 'active',
}

export interface MockPlayer {
  name: string
  /** Nivel 0–7 (see shared/levels.ts). */
  level: number
  position: 'Derecha' | 'Revés' | 'Ambos'
  city: string
  matches: number
  noShows: number
  /** Invite response signal: accepted / received (organizer trust KPI). */
  invitesAccepted: number
  invitesReceived: number
}

// Public-profile lookup for every name used in the mocks — powers the
// player-profile bottom sheet (tap any player anywhere).
export const mockPlayers: MockPlayer[] = [
  { name: 'Marce Ruiz', level: 3.4, position: 'Revés', city: 'Granada', matches: 47, noShows: 0, invitesAccepted: 18, invitesReceived: 20 },
  { name: 'Carlos Medina', level: 3.2, position: 'Derecha', city: 'Granada', matches: 12, noShows: 0, invitesAccepted: 8, invitesReceived: 10 },
  { name: 'Ana Pérez', level: 3.6, position: 'Revés', city: 'Granada', matches: 21, noShows: 1, invitesAccepted: 12, invitesReceived: 15 },
  { name: 'Jorge López', level: 3.8, position: 'Ambos', city: 'Granada', matches: 33, noShows: 0, invitesAccepted: 14, invitesReceived: 16 },
  { name: 'Lucía Ramos', level: 4.1, position: 'Derecha', city: 'Granada', matches: 34, noShows: 1, invitesAccepted: 10, invitesReceived: 14 },
  { name: 'Dani García', level: 4.4, position: 'Revés', city: 'Granada', matches: 18, noShows: 2, invitesAccepted: 6, invitesReceived: 11 },
  { name: 'Marta Soler', level: 1.2, position: 'Revés', city: 'Granada', matches: 8, noShows: 0, invitesAccepted: 4, invitesReceived: 5 },
  { name: 'Pablo Vega', level: 0.8, position: 'Derecha', city: 'Granada', matches: 4, noShows: 0, invitesAccepted: 2, invitesReceived: 3 },
  { name: 'Inés Torres', level: 1.5, position: 'Ambos', city: 'Granada', matches: 9, noShows: 0, invitesAccepted: 5, invitesReceived: 6 },
]


// In-session match overrides (design stub) — "Guardar cambios", "Abrir al
// público" and "Cancelar" must take effect EVERYWHERE the match is read in the
// same visit (one fact, one store — cert-walk discipline 2026-07-04).
const matchOverrides = new Map<string, Partial<MockMatch>>()

export function overrideMatch(id: string, patch: Partial<MockMatch>): void {
  matchOverrides.set(id, { ...matchOverrides.get(id), ...patch })
}

/** A match with its in-session overrides applied. */
export function withOverrides(m: MockMatch): MockMatch {
  const o = matchOverrides.get(m.id)
  return o ? { ...m, ...o } : m
}

// In-session profile overrides — "Guardar" in Editar perfil must be visible on
// Perfil immediately.
const profileOverrides: Partial<typeof mockProfile> = {}

export function overrideProfile(patch: Partial<typeof mockProfile>): void {
  Object.assign(profileOverrides, patch)
}

export function getProfile(): typeof mockProfile {
  return { ...mockProfile, ...profileOverrides }
}

export function findPlayer(name: string | undefined): MockPlayer | undefined {
  return mockPlayers.find((p) => p.name === name)
}

export function findMatch(id: string | undefined): MockMatch | undefined {
  const m = [...createdMatches, ...mockMatches, ...mockPastMatches, mockMyMatch, mockJoinedMatch].find(
    (x) => x.id === id,
  )
  return m ? withOverrides(m) : undefined
}

// In-session created matches — "Partido creado" must be TRUE: the new match
// appears in Mis partidos (and the open feed) immediately.
export const createdMatches: MockMatch[] = []

export function addCreatedMatch(m: MockMatch): void {
  createdMatches.unshift(m)
}

/** "Hoy" / "Mañana" / short weekday for a yyyy-mm-dd (design stub). */
export function dayLabelFrom(dateISO: string): string {
  const target = new Date(dateISO + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (diff === 0) return 'Hoy'
  if (diff === 1) return 'Mañana'
  const w = target.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '')
  return w.charAt(0).toUpperCase() + w.slice(1)
}

/** Free slots — ONE formula everywhere (players + off-app companions). */
export function freeSlots(m: MockMatch): number {
  return Math.max(m.capacity - m.players.length - (m.offAppCompanions ?? 0), 0)
}

/** Join/leave mutate the roster through the override store (one fact, one
 *  store) — ALWAYS with the current identity (getProfile), never the raw
 *  mockProfile constant (cert-walk-6: joins wrote a phantom 'Marce Ruiz'
 *  after onboarding under a new name). */
export function joinMatch(id: string): void {
  const m = findMatch(id)
  const me = getProfile().name
  if (m && !m.players.some((p) => p.name === me)) {
    overrideMatch(id, { players: [...m.players, { name: me }] })
  }
}

export function leaveMatch(id: string): void {
  const m = findMatch(id)
  const me = getProfile().name
  if (m) overrideMatch(id, { players: m.players.filter((p) => p.name !== me) })
}

/** Live profile stats — the base seed plus THIS session's activity, so a
 *  returning player sees the app visibly tracking them (cert-walk-8 C5). */
export function getProfileStats(): {
  matches: number
  streak: number
  noShows: number
  upcoming: number
  guestShows: number
  guestNoShows: number
} {
  const me = getProfile().name
  const pool = [...createdMatches, mockMyMatch, mockJoinedMatch, ...mockMatches, ...mockPastMatches].map((m) =>
    withOverrides(m),
  )
  const playedNow = pool.filter(
    (m) => m.status === 'past' && m.result && m.players.some((p) => p.name === me),
  ).length
  const upcoming = pool.filter(
    (m) => m.status === 'active' && (m.organizer === me || m.players.some((p) => p.name === me)),
  ).length
  const base = getProfile()
  // The stub has no vouching history, and inventing one would put a fabricated
  // trust claim on the design surface. Zero renders no line at all.
  return {
    matches: base.matches + playedNow,
    streak: base.streak,
    noShows: base.noShows,
    upcoming,
    guestShows: 0,
    guestNoShows: 0,
  }
}

/** Fresh-account reset: a brand-new identity starts with NO inherited session
 *  state (matches, overrides) — the returning-player demo seed belongs only to
 *  the default identity. */
export function resetMatchState(): void {
  createdMatches.length = 0
}

/** The CURRENT identity's own upcoming organized matches (invite targets). */
export function myUpcomingMatches(): MockMatch[] {
  const me = getProfile().name
  return [...createdMatches, mockMyMatch, mockJoinedMatch, ...mockMatches]
    .map((m) => withOverrides(m))
    .filter((m) => m.organizer === me && m.status === 'active')
}


export function findClub(id: string | undefined): MockClub | undefined {
  return mockClubs.find((c) => c.id === id)
}

// ── Bookability, for the dev bypass ─────────────────────────────────────────
//
// Only SOME clubs are bookable, mirroring production, where 38 of the first 48
// directory rows are places we listed ourselves that never connected to
// anything. A fixture where every club is bookable would make the contact-only
// branch — the majority case — unreachable by eye.
//
//   c1 Padel Sur       → bookable, SPLIT schedule (closes 14:00–17:00)
//   c2 Maristas Padel  → bookable, straight through, open late
//   c3, c4             → not bookable (the majority case)
//
// c1's lunch closure is deliberate: it is the case a naive first-open →
// last-close reading gets wrong, so the bypass walk shows whether the grid
// honours it.

/** Weekly `{periods}` in the stored shape: same window every day. */
function everyDay(open: [number, number], close: [number, number], closeNextDay = false) {
  return ([0, 1, 2, 3, 4, 5, 6] as const).map((d) => ({
    open: { day: d, hour: open[0], minute: open[1] },
    close: { day: closeNextDay ? ((d + 1) % 7) : d, hour: close[0], minute: close[1] },
  }))
}

/** One all-week rate band. Prices differ per club so the grid is not four
 *  identical numbers, which is how a price that never re-resolved would hide. */
function rate(price: number, startTime = '00:00', endTime = '23:59', priority = 0): PricingRuleInput {
  return {
    daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
    startTime,
    endTime,
    price,
    pricingMode: 'perHour',
    priority,
  }
}

const MOCK_BOOKING: Record<string, MockBookingFacts> = {
  c1: {
    clubManagerId: 'mock-club-1',
    timeZone: 'Europe/Madrid',
    openingHours: {
      periods: [...everyDay([9, 0], [14, 0]), ...everyDay([17, 0], [23, 0])],
      specialDays: [],
    },
    slotDurationMinutes: 90,
    courts: [
      { id: 'p1', name: 'Pista 1', pricingRules: [rate(24), rate(30, '18:00', '23:00', 10)] },
      { id: 'p2', name: 'Pista 2', pricingRules: [rate(24), rate(30, '18:00', '23:00', 10)] },
      { id: 'p3', name: 'Pista 3 (cristal)', pricingRules: [rate(28), rate(34, '18:00', '23:00', 10)] },
    ],
    clubDefaults: [],
    // Default window — exercises the fixed Hoy/Mañana case in the design walk.
    advanceBookingDays: 2,
  },
  c2: {
    clubManagerId: 'mock-club-2',
    timeZone: 'Europe/Madrid',
    openingHours: { periods: everyDay([8, 0], [0, 0], true), specialDays: [] },
    slotDurationMinutes: 60,
    courts: [
      { id: 'm1', name: 'Central', pricingRules: [rate(22), rate(29, '19:00', '22:00', 10)] },
      { id: 'm2', name: 'Pista 2', pricingRules: [rate(20), rate(26, '19:00', '22:00', 10)] },
    ],
    clubDefaults: [],
    // A wider window — c1 exercises the default 2-day case, c2 the extended
    // one, so a design walk can see both day-picker widths by eye.
    advanceBookingDays: 7,
  },
}

/** The shape `DirectoryClub.booking` carries. Structural rather than imported:
 *  `shared/` must not import from `features/` (lint-enforced). */
export interface MockBookingFacts {
  clubManagerId: string
  timeZone: string
  openingHours: unknown
  slotDurationMinutes: number
  courts: Array<{ id: string; name: string; pricingRules: PricingRuleInput[] }>
  clubDefaults: PricingRuleInput[]
  advanceBookingDays: number
}

export function mockBookingFactsFor(id: string): MockBookingFacts | null {
  return MOCK_BOOKING[id] ?? null
}
